import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { ModuleWithProviders, NgModule } from '@angular/core';
import { RouterModule, ROUTES } from '@angular/router';
import { EffectsModule } from '@ngrx/effects';
import { Action, MetaReducer, StoreModule } from '@ngrx/store';
import { UserManagerSettings } from 'oidc-client';
import { CardContainerComponent } from './components/card-container/card-container.component';
import { CardContainerService } from './components/card-container/card-container.service';
import { CardComponent } from './components/card/card.component';
import { CardService } from './components/card/card.service';
import { AuthInterceptor } from './services/auth.interceptor';
import { AuthService } from './services/auth.service';
import { AuthorizationService } from './services/authorization.service';
import { CompilationService } from './services/compilation.service';
import { ErrorLoggingInterceptor } from './services/error-logging.interceptor';
import { HMRService } from './services/hmr.service';
import { LoaderService } from './services/loader.service';
import { LogSettings, LOG_SETTINGS } from './services/log/log-settings';
import { LogService } from './services/log/log.service';
import { NavigationService } from './services/navigation.service';
import { PolarisService } from './services/polaris.service';
import { RoutingService } from './services/routing.service';
import { SenderIdService } from './services/senderid.service';
import { SettingsService } from './services/settings.service';
import { WebsocketGatewayService } from './services/websocket-gateway/websocket-gateway.service';
import { DevServersEffects } from './state/dev-servers/effects';
import { DevServersState } from './state/dev-servers/state';
import { FeaturesEffects } from './state/features/effects';
import { FeaturesState } from './state/features/state';
import { metaReducers, reducers } from './state/reducer';
import { RouterEffects } from './state/router/effects';
import { SettingsEffects } from './state/settings/effects';
import { SettingsState } from './state/settings/state';
import {
  APP_NAME,
  APP_VERSION,
  DEV_MODULES,
  ENVIRONMENT_CONFIG,
  OIDC_SETTINGS,
} from './tokens';
import { DevModule, EnvironmentConfig } from './types';

export interface ForRootParams {
  appName: string;
  appVersion: string;
  logSettings: LogSettings;
  devModules: DevModule[];
  oidcSettings: UserManagerSettings;
  envConfig?: EnvironmentConfig;
}

@NgModule({
  declarations: [CardComponent, CardContainerComponent],
  imports: [
    CommonModule,
    RouterModule,
    StoreModule.forFeature('northstar', reducers, {
      metaReducers: metaReducers as MetaReducer<
        {
          devServers: DevServersState;
          features: FeaturesState;
          settings: SettingsState;
        },
        Action
      >[],
    }),
    EffectsModule.forFeature([
      DevServersEffects,
      FeaturesEffects,
      SettingsEffects,
      RouterEffects,
    ]),
  ],
  exports: [CardContainerComponent, CardComponent],
})
export class NorthstarModule {
  static forRoot(params: ForRootParams): ModuleWithProviders<NorthstarModule> {
    return {
      ngModule: NorthstarModule,
      providers: [
        AuthService,
        AuthorizationService,
        CardService,
        CardContainerService,
        WebsocketGatewayService,
        LogService,
        HMRService,
        NavigationService,
        RoutingService,
        CompilationService,
        PolarisService,
        LoaderService,
        SettingsService,
        SenderIdService,
        { provide: ROUTES, useValue: params.devModules, multi: true },
        { provide: DEV_MODULES, useValue: params.devModules },
        { provide: OIDC_SETTINGS, useValue: params.oidcSettings },
        { provide: APP_NAME, useValue: params.appName },
        { provide: APP_VERSION, useValue: params.appVersion },
        { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
        {
          provide: HTTP_INTERCEPTORS,
          useClass: ErrorLoggingInterceptor,
          multi: true,
        },
        { provide: ENVIRONMENT_CONFIG, useValue: params.envConfig },
        { provide: LOG_SETTINGS, useValue: params.logSettings },
      ],
    };
  }
}
